/**
 * sak-reviews-lazy.js — mount the Yotpo reviews widget near the viewport (INP round 3, R3b X2, packet 220).
 *
 * snippets/sak-reviews-lazy.liquid (behind the "Reviews widget: load when scrolled near" setting) holds the Yotpo
 * Reviews app block's markup in an inert <template> inside [data-sak-reviews-lazy]. Rendering that widget cost one
 * 250–310 ms frame per PDP in the post-load burst (exch. 248); here it renders when it is about to be seen.
 *
 * Yotpo's v3 initializer scans for `.yotpo-widget-instance` elements when it loads; a div added later is NOT picked up
 * by its own observers (measured 2026-10-26: inserted and left alone for 20 s, never initialised) — it renders only
 * through `window.yotpoWidgetsContainer.initWidgets()` (rendered in ~1.0 s under Chrome 4×, ~0.5 s in WebKit). If the
 * initializer has not arrived yet when we mount, its own first scan finds the div.
 *
 * Paths that must land on rendered reviews (packet 220):
 *  - scroll: an IntersectionObserver with a 600px margin mounts the widget before it enters the viewport;
 *  - the star rating near the title: Yotpo's own click handler scrolls to `#yotpo-reviews-main-widget` only if it
 *    exists, so we mount on pointerdown (head start) and, if the root was still missing at click time, scroll + focus
 *    it ourselves once rendered (the same call Yotpo makes);
 *  - a hash on arrival or change whose text names the reviews (#reviews, #yotpo-reviews, #yotpo-reviews-main-widget):
 *    mount at once and scroll to the rendered root (the browser's own hash scroll found nothing);
 *  - the colour swap: while still held, the template's instance attributes follow the new product so the eventual
 *    mount renders the right reviews; once mounted the widget is today's markup and Yotpo's own pushState handling
 *    applies, as before this setting.
 */
(() => {
  if (window.__sakReviewsLazy) return;
  window.__sakReviewsLazy = true;

  const HOST = '[data-sak-reviews-lazy]';
  const MAIN = '#yotpo-reviews-main-widget';
  const STAR = '[id*="yotpo_product_reviews_star_rating"]';
  const RENDER_WAIT_MS = 15000;

  const host = () => document.querySelector(HOST);
  const heldTemplate = () => { const h = host(); return h ? h.querySelector('template') : null; };
  const mainRoot = () => document.querySelector(MAIN);
  const rendered = () => { const m = mainRoot(); return m && (m.textContent || '').trim().length > 0 ? m : null; };
  const waitFor = (fn, ms) => new Promise((resolve) => {
    const t0 = performance.now();
    const tick = () => { const v = fn(); if (v) return resolve(v); if (performance.now() - t0 > ms) return resolve(null); setTimeout(tick, 50); };
    tick();
  });

  let mounting = null;

  /** Put the held markup into the document and ask Yotpo to render it. Resolves with the rendered root (or null). */
  function mount(reason) {
    if (mounting) return mounting;
    const t = heldTemplate();
    if (!t) { mounting = waitFor(rendered, RENDER_WAIT_MS); return mounting; }
    const h = t.parentNode;
    t.replaceWith(t.content);
    h.setAttribute('data-sak-reviews-mounted', reason);
    const c = window.yotpoWidgetsContainer;
    if (c && typeof c.initWidgets === 'function') { try { c.initWidgets(); } catch (_) {} }
    mounting = waitFor(rendered, RENDER_WAIT_MS);
    return mounting;
  }

  function scrollToReviews(root) {
    if (!root) return;
    root.scrollIntoView({ behavior: 'smooth' });
    try { root.focus({ preventScroll: true }); } catch (_) {}
  }

  // ── near the viewport ────────────────────────────────────────────────────
  const h0 = host();
  if (h0 && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      mount('near');
    }, { rootMargin: '600px 0px' });
    io.observe(h0);
  } else if (h0) {
    mount('no-io');
  }

  // ── the star rating near the title ───────────────────────────────────────
  document.addEventListener('pointerdown', (e) => {
    if (e.target && e.target.closest && e.target.closest(STAR) && heldTemplate()) mount('star');
  }, { capture: true, passive: true });
  document.addEventListener('click', (e) => {
    if (!(e.target && e.target.closest && e.target.closest(STAR))) return;
    if (mainRoot()) return; // Yotpo's own handler finds the root and scrolls
    mount('star').then(scrollToReviews);
  }, true);

  // ── a reviews hash ───────────────────────────────────────────────────────
  const hashNamesReviews = () => /review/i.test(location.hash);
  const onHash = () => { if (hashNamesReviews() && !rendered()) mount('hash').then(scrollToReviews); };
  onHash();
  window.addEventListener('hashchange', onHash);

  // ── the colour swap ──────────────────────────────────────────────────────
  document.addEventListener('color-swap:complete', (e) => {
    const d = (e && e.detail) || {};
    const p = d.productData;
    const t = heldTemplate();
    if (!t || !p) return;
    const inst = t.content.querySelector('.yotpo-widget-instance');
    if (!inst) return;
    const set = (k, v) => { if (v != null && v !== '') inst.setAttribute(k, String(v)); };
    set('data-yotpo-product-id', p.id);
    set('data-yotpo-name', p.title);
    set('data-yotpo-url', d.url ? new URL(d.url, location.href).href.split('?')[0] : location.href.split('?')[0]);
    set('data-yotpo-price', typeof p.price === 'number' ? String(p.price / 100) : null);
    set('data-yotpo-image-url', p.featured_image);
    set('data-yotpo-description', typeof p.description === 'string' ? p.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : null);
  });

  window.sakReviewsLazy = { mount, rendered };
})();
